import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/auth/auth-context";
import { queryKeys, useTeamMembers } from "@/hooks/use-app-queries";
import { invalidateProjectData } from "@/lib/query-invalidation";
import { updateProject } from "@/lib/projects-clients";
import { createScheduleBlock } from "@/lib/schedule";

type PlanApprovedWorkContext = {
  projectId: string;
  projectName: string;
  clientName?: string | null;
  ticketNumber?: string | null;
  ticketType?: string | null;
  approvedAmount?: number | null;
  approvedAt?: string | null;
  assignedMemberId?: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: PlanApprovedWorkContext | null;
  onPlanned?: () => void;
};

function toDateInput(date: Date) {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function toIso(date: string, time: string) {
  return new Date(`${date}T${time}:00`).toISOString();
}

function addMinutes(time: string, minutes: number) {
  const [hour = "9", minute = "0"] = time.split(":");
  const date = new Date();
  date.setHours(Number(hour), Number(minute), 0, 0);
  date.setMinutes(date.getMinutes() + minutes);
  return `${date.getHours()}`.padStart(2, "0") + ":" + `${date.getMinutes()}`.padStart(2, "0");
}

function formatMoney(value?: number | null) {
  if (!Number.isFinite(Number(value))) return null;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(value));
}

export function PlanApprovedWorkDialog({ open, onOpenChange, context, onPlanned }: Props) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const teamQuery = useTeamMembers(open);
  const activeMembers = useMemo(
    () => (teamQuery.data ?? []).filter((member) => member.kind === "user" && member.status === "ACTIVE" && member.isActive),
    [teamQuery.data],
  );
  const today = useMemo(() => toDateInput(new Date()), []);
  const [assignedMemberId, setAssignedMemberId] = useState("unassigned");
  const [date, setDate] = useState(today);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const canManageAssignment = user?.currentPlan === "TEAM" && user.role === "ADMIN";

  useEffect(() => {
    if (!open) return;
    setAssignedMemberId(context?.assignedMemberId || "unassigned");
    setDate(today);
    setStartTime("09:00");
    setEndTime("10:00");
    setNotes("");
  }, [context?.assignedMemberId, open, today]);

  const savePlan = async () => {
    if (!context) return;
    const startAt = toIso(date, startTime);
    const endAt = toIso(date, endTime);
    if (new Date(endAt).getTime() <= new Date(startAt).getTime()) {
      toast({ title: "End time must be after start time.", variant: "destructive" });
      return;
    }

    const memberId = assignedMemberId === "unassigned" ? "" : assignedMemberId;
    setSaving(true);
    try {
      if (canManageAssignment) {
        await updateProject(context.projectId, { assignedMemberId: memberId });
      }
      await createScheduleBlock({
        projectId: context.projectId,
        startAt,
        endAt,
        notes,
        source: "APPROVAL_TRIGGER",
        assignedMemberId: memberId,
      });
      await Promise.all([
        invalidateProjectData(queryClient),
        queryClient.invalidateQueries({ queryKey: ["schedule-blocks"] }),
        queryClient.invalidateQueries({ queryKey: ["schedule-week-blocks"] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications(20) }),
      ]);
      toast({ title: "Work planned successfully." });
      onPlanned?.();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Could not save work plan",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const amount = formatMoney(context?.approvedAmount);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Plan Approved Work</DialogTitle>
          <DialogDescription>
            This ticket was approved. Assign responsibility and schedule the work.
          </DialogDescription>
        </DialogHeader>

        {context ? (
          <div className="space-y-5">
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-md bg-primary/10 p-2 text-primary">
                  <CalendarClock className="h-4 w-4" />
                </div>
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    {context.ticketNumber || "Approved ticket"}
                    {context.ticketType ? ` · ${context.ticketType}` : ""}
                  </p>
                  <p className="text-sm text-foreground">{context.projectName}</p>
                  <p className="text-xs text-muted-foreground">
                    Client: {context.clientName || "Client"}
                    {amount ? ` · Approved amount: ${amount}` : ""}
                  </p>
                </div>
              </div>
            </div>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Assign responsibility</h3>
              <div className="space-y-1.5">
                <Label>Assigned to</Label>
                <Select value={assignedMemberId} onValueChange={setAssignedMemberId} disabled={!canManageAssignment}>
                  <SelectTrigger>
                    <SelectValue placeholder="Unassigned for now" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned for now</SelectItem>
                    {activeMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {(member.fullName || member.email) + ` - ${member.role}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Schedule work</h3>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Date</Label>
                  <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Start time</Label>
                  <Input
                    type="time"
                    value={startTime}
                    onChange={(event) => {
                      setStartTime(event.target.value);
                      setEndTime(addMinutes(event.target.value, 60));
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>End time</Label>
                  <Input type="time" value={endTime} onChange={(event) => setEndTime(event.target.value)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={2} />
              </div>
            </section>
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void savePlan()} disabled={saving || !context}>
            {saving ? "Saving..." : "Save plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
